// Lightweight gtag wrapper — sends events to GA4 and Google Ads conversions
import CLINIC_CONFIG from '../config/clinicConfig';

// GA4 Measurement ID
export const GA_MEASUREMENT_ID = CLINIC_CONFIG.gaMeasurementId;

// Google Ads conversion config (AW- tag loaded in index.html)
const GOOGLE_ADS_CONVERSIONS = CLINIC_CONFIG.googleAdsConversions;

type GtagEventParams = Record<string, string | number | boolean | undefined>;

// ── SPA page view tracking ──────────────────────────────────────────────
// Tracks every React Router navigation with the previous page path.
// This gives GA4 the full user journey (e.g. /dental-cleaning → /book-appointment).
let previousPagePath: string = '';

/** Call on every route change to send a page_view with previous page */
export function trackPageView(currentPath: string) {
  if (typeof window === 'undefined') return;
  if (typeof (window as any).gtag !== 'function') return;

  (window as any).gtag('event', 'page_view', {
    page_path: currentPath,
    page_location: window.location.href,
    page_referrer: previousPagePath || document.referrer || 'direct',
  });

  previousPagePath = currentPath;
}

/** Fire a GA4 event via the global gtag() function loaded in index.html */
export function trackEvent(eventName: string, params?: GtagEventParams) {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', eventName, params);
  }
}

/** Fire a Google Ads conversion event (if a label is configured for this event) */
function trackConversion(eventName: string) {
  if (typeof window === 'undefined') return;
  if (typeof (window as any).gtag !== 'function') return;

  const sendTo = GOOGLE_ADS_CONVERSIONS[eventName];
  if (!sendTo) return;

  (window as any).gtag('event', 'conversion', { send_to: sendTo });
}

/** Fire both GA4 event + Google Ads conversion */
function trackEventWithConversion(eventName: string, params?: GtagEventParams) {
  trackEvent(eventName, params);
  trackConversion(eventName);
}

// ── Pre-built event helpers ─────────────────────────────────────────────
// All events fire to both GA4 and Google Ads.
// Primary vs secondary classification is configured in Google Ads (not here).

export const trackPhoneClick = (pageUrl?: string) =>
  trackEventWithConversion('phone_call_click', { link_url: `tel:${CLINIC_CONFIG.phoneNumberRaw}`, page_location: pageUrl ?? window.location.href });

export const trackSmsClick = (smsUrl: string, pageUrl?: string) =>
  trackEventWithConversion('sms_click', { link_url: smsUrl, page_location: pageUrl ?? window.location.href });

export const trackDirectionsClick = (pageUrl?: string) =>
  trackEventWithConversion('directions_click', { page_location: pageUrl ?? window.location.href });

export const trackEmailClick = (pageUrl?: string) =>
  trackEventWithConversion('email_click', { page_location: pageUrl ?? window.location.href });

export const trackMapClick = (pageUrl?: string) =>
  trackEventWithConversion('map_click', { link_url: 'google_maps', page_location: pageUrl ?? window.location.href });

export const trackSocialClick = (platform: string, url: string) =>
  trackEventWithConversion('social_media_click', { link_url: url, social_platform: platform, page_location: window.location.href });

export const trackFinancingClick = (provider: string, url: string) =>
  trackEventWithConversion('financing_click', { link_url: url, provider, page_location: window.location.href });

export const trackMembershipClick = (url: string) =>
  trackEventWithConversion('membership_click', { link_url: url, page_location: window.location.href });

export const trackOutboundClick = (url: string, text?: string) =>
  trackEventWithConversion('outbound_link_click', { link_url: url, link_text: text, page_location: window.location.href });

export const trackChatSend = () =>
  trackEventWithConversion('chat_send', { page_location: window.location.href });

export const trackChatVoiceCall = () =>
  trackEventWithConversion('chatbot_voice_call', { page_location: window.location.href });

export const trackChatInsuranceUpload = () =>
  trackEventWithConversion('chatbot_insurance_upload', { page_location: window.location.href });

export const trackChatQuickAction = (text: string) =>
  trackEventWithConversion('chatbot_quick_action', { click_text: text, page_location: window.location.href });

export const trackChatPhoneClick = (url: string) =>
  trackEventWithConversion('chatbot_phone_click', { link_url: url, page_location: window.location.href });

// ── Funnel / journey tracking ────────────────────────────────────────────
// These capture the user's path: which page they came from → booking/portal → success

/** Get the previous page path the user navigated from */
function getReferrerPage(): string {
  if (previousPagePath) return previousPagePath;
  // Fallback to document.referrer for first page load
  try {
    const ref = document.referrer;
    if (!ref) return 'direct';
    const url = new URL(ref);
    if (url.origin === window.location.origin) return url.pathname;
    return `external:${url.hostname}`;
  } catch { return 'direct'; }
}

/** User landed on /book-appointment page */
export const trackViewBookAppointment = () =>
  trackEvent('view_book_appointment', {
    page_location: window.location.href,
    page_referrer: getReferrerPage(),
  });

/** User completed step 1 (patient info) on /book-appointment */
export const trackBookingStepPatientInfo = (isNewPatient: boolean) =>
  trackEvent('booking_step_patient_info', {
    page_location: window.location.href,
    is_new_patient: isNewPatient,
  });

/** User completed step 2 (contact info) — new patients only */
export const trackBookingStepContactInfo = () =>
  trackEvent('booking_step_contact_info', { page_location: window.location.href });

/** User landed on /patient-portal page */
export const trackViewPatientPortal = () =>
  trackEvent('view_patient_portal', {
    page_location: window.location.href,
    page_referrer: getReferrerPage(),
  });

/** User logged into patient portal */
export const trackPatientPortalLogin = () =>
  trackEvent('patient_portal_login', { page_location: window.location.href });

/** User opened booking dropdown inside patient portal */
export const trackPortalBookStart = () =>
  trackEvent('portal_book_start', { page_location: window.location.href });

export const trackBookAppointmentSuccess = () =>
  trackEventWithConversion('book_appointment_success', { page_location: window.location.href });

export const trackContactFormSuccess = () =>
  trackEventWithConversion('contact_form_success', { page_location: window.location.href });

export const trackCallbackSuccess = () =>
  trackEventWithConversion('call_back_success', { page_location: window.location.href });

export const trackConfirmBooking = () =>
  trackEventWithConversion('Confirm-Booking', { page_location: window.location.href });

export const trackSubmitPayment = () =>
  trackEventWithConversion('Submit-Payment', { page_location: window.location.href });
